import { action, observable, computed } from 'mobx';
import debounce from 'lodash-decorators/debounce';
import keyBy from 'lodash/keyBy';
import { ParticipantType } from 'components/LiveClass/utils/consts';
import {
  fetchCurrentCard,
  fetchCurrentCardSubscription
} from 'components/LiveClass/requests/fetchCurrentCard';
import { fetchCompletedCardsSubscription } from 'components/LiveClass/requests/fetchCompletedCards';
import updateCurrentCard from 'components/LiveClass/requests/updateCurrentCard';
import updateCompletedCards from 'components/LiveClass/requests/updateCompletedCards';
import CardCompletionInfoStore from './CardCompletionInfoStore';

class ProgressSyncStore {
  @observable.ref currentCardId = '';

  // [CardCompletionInfo]
  @observable.ref completionInfo = [];

  unsubscribeCurrentCardSubscription = null;

  unsubcribeProgressSubscription = null;

  constructor(store) {
    this.store = store;
  }

  @computed
  get completionInfoByCardId() {
    return keyBy(this.completionInfo, 'cardId');
  }

  @action
  async init({ classId, currentCardId: cardId, participantType, twilioRoom }) {
    twilioRoom
      .off('disconnected', this.handleRoomDisconnect)
      .on('disconnected', this.handleRoomDisconnect);

    if (participantType === ParticipantType.Teacher) {
      const currentCardId = await fetchCurrentCard(classId);
      if (!currentCardId) {
        this.updateCurrentCardId(cardId);
      } else {
        this.currentCardId = currentCardId;
      }

      this.unsubcribeProgressSubscription = await fetchCompletedCardsSubscription(
        classId,
        this.handleCompletedCardsChange
      );
    } else {
      this.unsubscribeCurrentCardSubscription = await fetchCurrentCardSubscription(
        classId,
        this.handleCurrentCardChange
      );
    }
  }

  @action
  handleCurrentCardChange = (cardId) => {
    this.currentCardId = cardId;
  };

  @action
  handleCompletedCardsChange = (events) => {
    events.forEach(({ cardId, userId }) => {
      let cardCompletionInfo = this.completionInfo.find(c => c.cardId === cardId);
      if (!cardCompletionInfo) {
        cardCompletionInfo = new CardCompletionInfoStore(this.store, cardId);
        this.completionInfo = [...this.completionInfo, cardCompletionInfo];
      }

      if (!cardCompletionInfo.allCompletedUserIds.includes(userId)) {
        cardCompletionInfo.allCompletedUserIds = [
          ...cardCompletionInfo.allCompletedUserIds,
          userId
        ];
      }
    });
  };

  @debounce(300)
  @action
  updateCurrentCardId = (cardId) => {
    this.currentCardId = cardId;

    const { classId } = this.store.room;
    const { userId, companyId } = this.store;

    updateCurrentCard(classId, cardId, userId, companyId);
  };

  @action
  updateCompletedCards = (completedCardIds, courseId) => {
    const { classId } = this.store.room;
    const { userId, companyId } = this.store;

    return Promise.all(
      // prettier-ignore
      completedCardIds.map(
        cardId => updateCompletedCards(classId, userId, companyId, courseId, cardId)
      )
    );
  };

  // Release resources
  handleRoomDisconnect = () => {
    this.completionInfo = [];
    this.unsubscribeCurrentCardSubscription && this.unsubscribeCurrentCardSubscription();
    this.unsubcribeProgressSubscription && this.unsubcribeProgressSubscription();
  };
}

export default ProgressSyncStore;
